import { type ReactNode } from 'react';
import styled from 'styled-components';

import { Text } from '../../atoms';

type VerticalTimelineItem =
	| {
			content: ReactNode;
			text?: never;
			title: string;
	  }
	| {
			content?: never;
			text: string;
			title: string;
	  };

type VerticalTimelineProps = {
	className?: string;
	items: Array<VerticalTimelineItem>;
	testID?: string;
};

export const VerticalTimeline = ({ className, items, testID }: VerticalTimelineProps) => {
	return (
		<S.TimelineContainer className={className} data-testid={testID}>
			{items.map((item, index) => (
				<S.TimelineItemContainer key={index}>
					<S.TimelineMarker>
						<S.TimelineDot />
						<S.TimelineLine $isLast={index === items.length - 1} />
					</S.TimelineMarker>
					<S.TimelineContent>
						<S.TimelineTitle variant="title6">{item.title}</S.TimelineTitle>
						<S.TimelineDescription>
							{item.text ? <Text variant="text3">{item.text}</Text> : item.content}
						</S.TimelineDescription>
					</S.TimelineContent>
				</S.TimelineItemContainer>
			))}
		</S.TimelineContainer>
	);
};

namespace S {
	export const TimelineContainer = styled.div`
		display: flex;
		flex-direction: column;
		padding: 0;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const TimelineItemContainer = styled.div`
		display: flex;
		gap: ${({ theme }) => theme.spacings.small};
		position: relative;
	`;

	export const TimelineMarker = styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		flex-shrink: 0;
	`;

	export const TimelineDot = styled.div`
		width: 12px;
		height: 12px;
		margin-top: ${({ theme }) => theme.spacings.xxxsmall};
		background-color: ${({ theme }) => theme.colors.icons.brand.enabled};
		border-radius: 50%;
		position: relative;
		z-index: 2;
	`;

	export const TimelineLine = styled.div<{ $isLast: boolean }>`
		width: 1px;
		background-color: ${({ theme }) => theme.colors.borders.brand.enabled};
		flex-grow: 1;
		margin-top: ${({ theme }) => theme.spacings.xxxsmall};
		opacity: ${({ $isLast }) => ($isLast ? 0 : 1)};
	`;

	export const TimelineContent = styled.div`
		flex: 1;
		padding-top: 0;
		margin-bottom: ${({ theme }) => theme.spacings.medium};
	`;

	export const TimelineTitle = styled(Text)`
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const TimelineDescription = styled.div`
		margin: 0;
		white-space: pre-wrap;
	`;
}
